import { CommonModule } from '@angular/common';
import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
    input,
    output,
    viewChild,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { DateRangeFilterComponent } from '../date-range-filter/date-range-filter.component';
import { SingleSelectionEntityComponent } from '../single-selection-entity/single-selection-entity.component';
import { format } from 'date-fns';
import { EntityFilter } from '../../../admin/models/entity-filter';
import { COMMON_KENDO_IMPORTS } from '../../../../common/common-imports';

export interface AggregationOption {
    label: string;
    data: string[];
    selectedValue?: string;
}

@Component({
    selector: 'app-base-export-modal',
    templateUrl: './base-export-modal.component.html',
    styleUrls: ['./base-export-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        DateRangeFilterComponent,
        SingleSelectionEntityComponent,
        ...COMMON_KENDO_IMPORTS,
    ],
})
export class BaseExportModalComponent implements OnChanges {
    readonly entitySelector = viewChild(SingleSelectionEntityComponent);
    readonly dateRangeFilter = viewChild(DateRangeFilterComponent);

    readonly title = input<string>('Data Export');
    @Input() startDate?: Date;
    @Input() endDate?: Date;
    @Input() selectedEntity?: EntityFilter;
    @Input() aggregationOptions: AggregationOption[] = [];

    readonly entityChanged = output<EntityFilter>();
    readonly export = output<{
        startDate: Date | undefined;
        endDate: Date | undefined;
        aggregations: Record<string, string>;
        selectedEntity?: EntityFilter;
    }>();
    readonly clearFiltersRequested = output<void>();

    exportWindowOpen$ = new BehaviorSubject<boolean>(false);
    dateRangeFormGroup: FormGroup;

    constructor() {
        this.dateRangeFormGroup = new FormGroup({
            beginDate: new FormControl(null, Validators.required),
            endDate: new FormControl(null, Validators.required),
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['startDate'] && this.startDate) {
            const formattedDate = format(this.startDate, 'yyyy-MM-dd');
            this.dateRangeFormGroup.get('beginDate')?.setValue(formattedDate);
        }
        if (changes['endDate'] && this.endDate) {
            const formattedDate = format(this.endDate, 'yyyy-MM-dd');
            this.dateRangeFormGroup.get('endDate')?.setValue(formattedDate);
        }
    }

    openExportWindow(selectedEntity: EntityFilter): void {
        // Update form with current input values when opening
        if (this.startDate) {
            const formattedDate = format(this.startDate, 'yyyy-MM-dd');
            this.dateRangeFormGroup.get('beginDate')?.setValue(formattedDate);
        }
        if (this.endDate) {
            const formattedDate = format(this.endDate, 'yyyy-MM-dd');
            this.dateRangeFormGroup.get('endDate')?.setValue(formattedDate);
        }
        this.selectedEntity = selectedEntity;
        this.exportWindowOpen$.next(true);
        // The entity selector lives inside *ngIf and is created after this tick.
        // Defer syncing so we overwrite its default selection with the parent value.
        this.syncSelectedEntityToSelector();
    }

    private syncSelectedEntityToSelector(): void {
        if (!this.selectedEntity) {
            return;
        }
        const entity = this.selectedEntity;
        Promise.resolve().then(() => {
            this.entitySelector()?.writeValue(entity);
        });
    }

    closeExportWindow(): void {
        this.exportWindowOpen$.next(false);
    }

    clearFilters(): void {
        this.clearFiltersRequested.emit();
    }

    exportData(): void {
        const startDate = this.dateRangeFilter()?.startDate ?? undefined;
        const endDate = this.dateRangeFilter()?.endDate ?? undefined;
        const selectedEntity =
            this.entitySelector()?.selectedFilter ?? undefined;

        const aggregations: Record<string, string> = {};
        this.aggregationOptions.forEach((option) => {
            if (option.selectedValue) {
                aggregations[option.label] = option.selectedValue;
            }
        });

        this.export.emit({
            startDate,
            endDate,
            aggregations,
            selectedEntity,
        });
    }

    onEntityChanged(entity: EntityFilter): void {
        this.selectedEntity = entity;
        this.entityChanged.emit(entity);
    }
}
